/* eslint-disable */
import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Autocomplete,
  Button,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
  Paper,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import { fetcher, patchFetcher } from 'src/utils/axios';
import { useSnackbar } from 'src/components/snackbar';
import Iconify from 'src/components/iconify';

// ─── Shared design tokens ──────────────────────────────────────────────────────
const RADIUS = '14px';
const PILL = '50px';
const SECTION_BG = '#f9f9fb';
const BORDER = '1.5px solid #ebebef';
const ACCENT = '#6366f1';
const ACCENT_LIGHT = '#eef2ff';
const DANGER = '#ef4444';
const DANGER_LIGHT = '#fef2f2';
const TEXT_MAIN = '#18181b';
const TEXT_SUB = '#71717a';
const CARD_SHADOW = '0 1px 4px rgba(0,0,0,0.06)';

// ─── Sub-components ────────────────────────────────────────────────────────────

const SectionCard = ({ title, icon, children, noPad }) => (
  <Box sx={{
    background: '#fff',
    border: BORDER,
    borderRadius: RADIUS,
    boxShadow: CARD_SHADOW,
    mb: 2,
    overflow: 'hidden',
  }}>
    {title && (
      <Box sx={{
        px: 2.5, py: 1.5,
        borderBottom: BORDER,
        display: 'flex', alignItems: 'center', gap: 1,
        background: SECTION_BG,
      }}>
        {icon && (
          <Box sx={{
            width: 28, height: 28, borderRadius: '8px',
            background: ACCENT_LIGHT,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Iconify icon={icon} width={15} sx={{ color: ACCENT }} />
          </Box>
        )}
        <Typography sx={{ fontWeight: 700, fontSize: '13px', color: TEXT_MAIN, letterSpacing: '-0.01em' }}>
          {title}
        </Typography>
      </Box>
    )}
    <Box sx={{ p: noPad ? 0 : 2.5 }}>{children}</Box>
  </Box>
);

const PillInput = ({ label, value, onChange, onKeyDown, multiline, rows, helperText, placeholder, disabled }) => (
  <TextField
    label={label}
    value={value}
    onChange={onChange}
    onKeyDown={onKeyDown}
    placeholder={placeholder}
    helperText={helperText}
    disabled={disabled}
    multiline={multiline}
    rows={rows}
    fullWidth
    variant="outlined"
    size="small"
    sx={{
      '& .MuiOutlinedInput-root': {
        borderRadius: multiline ? RADIUS : PILL,
        background: SECTION_BG,
        fontSize: '13.5px',
        '& fieldset': { borderColor: '#e4e4e7' },
        '&:hover fieldset': { borderColor: '#a1a1aa' },
        '&.Mui-focused fieldset': { borderColor: ACCENT, borderWidth: '1.5px' },
      },
      '& .MuiInputLabel-root': { fontSize: '13px' },
      '& .MuiFormHelperText-root': { fontSize: '11.5px', color: TEXT_SUB },
    }}
  />
);

const PillAutocomplete = ({ label, options, value, onChange, helperText, disabled, multiple = true }) => (
  <Autocomplete
    multiple={multiple}
    options={options}
    getOptionLabel={(option) => option.label || ''}
    value={value}
    onChange={onChange}
    disabled={disabled}
    size="small"
    isOptionEqualToValue={(option, value) => {
      // Compare by ID if available
      if (option?.id && value?.id) {
        return option.id === value.id;
      }
      // Compare by value field if available
      if (option?.value && value?.value) {
        return option.value === value.value;
      }
      // Fallback to strict equality
      return option === value;
    }}
    renderInput={(params) => (
      <TextField
        {...params}
        label={label}
        helperText={helperText}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: RADIUS,
            background: SECTION_BG,
            fontSize: '13.5px',
            '& fieldset': { borderColor: '#e4e4e7' },
            '&:hover fieldset': { borderColor: '#a1a1aa' },
            '&.Mui-focused fieldset': { borderColor: ACCENT, borderWidth: '1.5px' },
          },
          '& .MuiInputLabel-root': { fontSize: '13px' },
          '& .MuiFormHelperText-root': { fontSize: '11.5px', color: TEXT_SUB },
        }}
      />
    )}
    fullWidth
  />
);

const ActionButton = ({ children, onClick, color = 'primary', variant = 'contained', icon, disabled, danger }) => (
  <Button
    variant={variant}
    onClick={onClick}
    disabled={disabled}
    fullWidth
    startIcon={icon ? <Iconify icon={icon} width={16} /> : undefined}
    sx={{
      borderRadius: PILL,
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '13.5px',
      py: 1.1,
      boxShadow: 'none',
      ...(danger ? {
        background: variant === 'contained' ? DANGER : 'transparent',
        color: variant === 'contained' ? '#fff' : DANGER,
        borderColor: DANGER,
        '&:hover': { background: variant === 'contained' ? '#dc2626' : DANGER_LIGHT, boxShadow: 'none' },
      } : color === 'primary' ? {
        background: variant === 'contained' ? ACCENT : 'transparent',
        color: variant === 'contained' ? '#fff' : ACCENT,
        borderColor: ACCENT,
        '&:hover': { background: variant === 'contained' ? '#4f46e5' : ACCENT_LIGHT, boxShadow: 'none' },
      } : {}),
      '&:disabled': { opacity: 0.45 },
    }}
  >
    {children}
  </Button>
);

/**
 * Pure helper: reconstructs formula objects into a readable string.
 * Returns '' for unknown / invalid input.
 */
const reconstructFormulaString = (formula) => {
  if (!formula || !formula.operands || !formula.operation || !Array.isArray(formula.operands)) {
    return '';
  }

  const operationMap = {
    add: '+',
    subtract: '-',
    multiply: '*',
    divide: '/',
  };
  const operator = operationMap[formula.operation] || formula.operation;

  const processOperand = (operand) => {
    if (typeof operand === 'string') {
      return operand;
    }
    if (operand && operand.operation && operand.operands) {
      return `(${reconstructFormulaString(operand)})`;
    }
    return '';
  };

  const processedOperands = formula.operands
    .slice()
    .reverse()
    .map(processOperand)
    .filter((op) => op !== '');

  return processedOperands.join(` ${operator} `);
};

const NodePropertiesDrawer2ndStep = ({
  open,
  onClose,
  selectedNode,
  loadingNodeData,
  allVariantIds,
  setAllVariantIds,
  editNodeName,
  setEditNodeName,
  editNodeInputType,
  setEditNodeInputType,
  editRawMaterialInputs,
  setEditRawMaterialInputs,
  editPreviousNodeInputs,
  setEditPreviousNodeInputs,
  editUtilityInputs,
  setEditUtilityInputs,
  editNodeCost,
  setEditNodeCost,
  inventoryOptions,
  utilityOptions,
  costOptions,
  sourceNodeOptions,
  connectedEdges,
  selectedEdgeId,
  setSelectedEdgeId,
  editEdgeLabel,
  setEditEdgeLabel,
  handleUpdateNode,
  handleErrorEdgeButtonClick,
  handleDeleteSelectedNode,
  handleAddOrEditEdgeLabel,
  handleRemoveEdge,
  handleOutputDialog,
  handleInputFormulaDialog,
  handleRawMaterialDialog,
  handleUtilityDialog,
  handleCostDialog,
  isErrorEdgeMode,
  setselectedVariantsfromparent,
  selectedVariantsfromparent,
  seteditoutputids,
  editoutputids,
  setutitilityfromdrawer,
  nodes,
  isEditing,
  setIsEditing,
  clickednodes,
  setclickednodes,
  selectedworkflowId,
  onRegularVariantsSaved,
  onAllocableVariantsSaved,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  // ─── Local state ───────────────────────────────────────────────────────────────
  const [fetchedNodeData, setFetchedNodeData] = useState(null);
  const [fetchingNodeData, setFetchingNodeData] = useState(false);
  const [variantDialogOpen, setVariantDialogOpen] = useState(false);
  const [rawMaterialIds, setRawMaterialIds] = useState([]);
  const [loadingVariants, setLoadingVariants] = useState({});
  const [variantOptions, setVariantOptions] = useState({});
  const [editutitilty, seteditutitilty] = useState([]);
  const [hasrawmaterial, sethasrawmaterial] = useState(false);
  const [shownNotifications, setShownNotifications] = useState([]);
  const [fieldsEnabled, setFieldsEnabled] = useState(false);
  const [nodeVariants, setNodeVariants] = useState({});
  const [selectedVariants, setSelectedVariants] = useState({});
  const [nodeVariantMap, setNodeVariantMap] = useState({});
  const [arrayofoutputids, setarrayofoutputids] = useState([]);
  const [allocableVariantDialogOpen, setAllocableVariantDialogOpen] = useState(false);
  const [allocableVariantOptions, setAllocableVariantOptions] = useState({});
  const [selectedAllocableVariants, setSelectedAllocableVariants] = useState({}); // rawMaterialId → VariantObject[]
  const [loadingAllocable, setLoadingAllocable] = useState({});

  // FIXED: Access data from selectedNode.data
  const currentNodeId = selectedNode?.data?.backendId;
  const currentNodeName = selectedNode?.data?.label;

  // ─── Effects ───────────────────────────────────────────────────────────────────

  // Notification when opening drawer for unsaved node
  useEffect(() => {
    if (
      open &&
      currentNodeId &&
      !clickednodes.includes(currentNodeId) &&
      !shownNotifications.includes(currentNodeId)
    ) {
      enqueueSnackbar(
        `Please click "Update Node" for ${currentNodeName || 'this node'} to enable the save button.`,
        { variant: 'info', autoHideDuration: 5000 }
      );
      setShownNotifications((prev) => [...prev, currentNodeId]);
    }
  }, [open, currentNodeId, currentNodeName, clickednodes, shownNotifications, enqueueSnackbar]);

  useEffect(() => {
    if (currentNodeId && clickednodes.includes(currentNodeId)) {
      setFieldsEnabled(true);
    }
  }, [currentNodeId, clickednodes]);

  useEffect(() => {
    if (open && currentNodeId) {
      const isNodeUpdated = clickednodes.includes(currentNodeId);
      setFieldsEnabled(isNodeUpdated);
    }
  }, [open, currentNodeId, clickednodes]);

  const updateSelectedVariants = useCallback(
    (newVariants) => {
      setSelectedVariants(newVariants);
      if (currentNodeId) {
        setNodeVariants((prev) => ({
          ...prev,
          [currentNodeId]: newVariants,
        }));
      }
    },
    [currentNodeId]
  );

  // ─── Fetch regular variants ────────────────────────────────────────────────────
  const fetchVariantsForRawMaterial = useCallback(
    async (rawMaterialId) => {
      if (!rawMaterialId) return;
      setLoadingVariants((prev) => ({ ...prev, [rawMaterialId]: true }));

      try {
        const res = await fetcher([`/new/workflow-app/products/variants/list/${rawMaterialId}/`]);
        const variants = res?.results || res || [];
        setVariantOptions((prev) => ({ ...prev, [rawMaterialId]: variants }));

        setSelectedVariants((current) => {
          const curr = current[rawMaterialId] || [];
          const updated = curr
            .map((sel) => {
              if (sel?.id && sel?.name) return sel;
              return variants.find((v) => v.id === sel) || sel;
            })
            .filter(Boolean);

          if (JSON.stringify(curr) !== JSON.stringify(updated)) {
            const merged = { ...current, [rawMaterialId]: updated };
            if (currentNodeId) {
              setNodeVariants((p) => ({ ...p, [currentNodeId]: merged }));
            }
            return merged;
          }
          return current;
        });

        setSelectedVariants((prev) => ({
          ...prev,
          [rawMaterialId]: prev[rawMaterialId] || [],
        }));
      } catch (error) {
        console.error(`Error fetching variants for ${rawMaterialId}:`, error);
        enqueueSnackbar(`Failed to fetch variants for ${rawMaterialId}`, { variant: 'error' });
        setVariantOptions((prev) => ({ ...prev, [rawMaterialId]: [] }));
      } finally {
        setLoadingVariants((prev) => ({ ...prev, [rawMaterialId]: false }));
      }
    },
    [enqueueSnackbar, currentNodeId]
  );

  // ─── Fetch ALLOCABLE variants ──────────────────────────────────────────────────
  const fetchAllocableVariantsForRawMaterial = useCallback(
    async (rawMaterialId) => {
      if (!rawMaterialId) return;
      setLoadingAllocable((prev) => ({ ...prev, [rawMaterialId]: true }));

      try {
        const res = await fetcher([
          `/new/workflow-app/products/variants/list/is/allocable/${rawMaterialId}/`
        ]);
        const variants = Array.isArray(res) ? res : [];
        setAllocableVariantOptions((prev) => ({ ...prev, [rawMaterialId]: variants }));
      } catch (error) {
        console.error(`Error fetching allocable variants for ${rawMaterialId}:`, error);
        enqueueSnackbar(`Failed to load allocable variants for ${rawMaterialId}`, { variant: 'error' });
        setAllocableVariantOptions((prev) => ({ ...prev, [rawMaterialId]: [] }));
      } finally {
        setLoadingAllocable((prev) => ({ ...prev, [rawMaterialId]: false }));
      }
    },
    [enqueueSnackbar]
  );

  const initializeNodeVariants = useCallback(
    (nodeId, nodeData) => {
      if (!nodeId || !nodeData) return;

      setNodeVariants((prev) => {
        if (prev[nodeId]) return prev;
        let initial = {};

        const inputForeign = nodeData?.input_foreign?.[0];
        if (inputForeign?.value?.product_ids && inputForeign?.product_variant_data) {
          const pids = inputForeign.value.product_ids || [];
          const pvData = inputForeign.product_variant_data || [];
          pids.forEach((pid, i) => {
            initial[pid] = pvData[i] ? [pvData[i]] : [];
          });
        } else if (inputForeign?.variants) {
          initial = inputForeign.variants;
        }

        return { ...prev, [nodeId]: initial };
      });

      const inputForeign = nodeData?.input_foreign?.[0];
      let initialSelected = {};
      if (inputForeign?.value?.product_ids && inputForeign?.product_variant_data) {
        const pids = inputForeign.value.product_ids || [];
        const pvData = inputForeign.product_variant_data || [];
        pids.forEach((pid, i) => {
          initialSelected[pid] = pvData[i] ? [pvData[i]] : [];
        });
      } else if (inputForeign?.variants) {
        initialSelected = inputForeign.variants;
      }
      updateSelectedVariants(initialSelected);
    },
    [updateSelectedVariants]
  );

  // Fetch node data
  useEffect(() => {
    const fetchNodeData = async () => {
      if (!selectedNode?.data?.backendId) return;

      setFetchingNodeData(true);
      try {
        const id = selectedNode.data.backendId;
        const res = await fetcher([`/new/workflow-app/node/${id}/`]);

        seteditoutputids(res?.output_foreign);
        setFetchedNodeData(res);

        const productIds = res?.input_foreign?.[0]?.value?.product_ids || [];
        const product_variant_ids = res?.input_foreign?.[0]?.value?.product_variant_ids || [];
        const utilityIds = res?.input_foreign?.[0]?.value?.utility_ids || [];

        seteditutitilty(utilityIds);
        if (setutitilityfromdrawer) setutitilityfromdrawer(utilityIds);

        if (Array.isArray(productIds) && productIds.length > 0) {
          sethasrawmaterial(true);
          await Promise.all(productIds.map(fetchVariantsForRawMaterial));
        } else {
          sethasrawmaterial(false);
        }

        initializeNodeVariants(id, res);

        if (Array.isArray(product_variant_ids) && product_variant_ids.length > 0) {
          setNodeVariantMap((prev) => ({ ...prev, [id]: product_variant_ids }));
        }
      } catch (error) {
        console.error('Error fetching node data:', error);
        enqueueSnackbar('Failed to fetch node data', { variant: 'error' });
        setFetchedNodeData(null);
        seteditutitilty([]);
      } finally {
        setFetchingNodeData(false);
      }
    };

    if (open) fetchNodeData();
  }, [
    open,
    selectedNode,
    enqueueSnackbar,
    seteditoutputids,
    fetchVariantsForRawMaterial,
    initializeNodeVariants,
    setutitilityfromdrawer,
  ]);

  // ─── Utilities ─────────────────────────────────────────────────────────────────
  const selectedUtilities = useMemo(() => {
    if (!editutitilty || !Array.isArray(editutitilty) || !utilityOptions) return [];
    return utilityOptions.filter((opt) => editutitilty.includes(Number(opt.id)));
  }, [editutitilty, utilityOptions]);

  useEffect(() => {
    if (currentNodeId) {
      setSelectedVariants(nodeVariants[currentNodeId] || {});
    }
  }, [currentNodeId, nodeVariants]);

  // sync regular **and allocable** selections into the global list so that
  // the parent component can use `allVariantIds` as a single source of truth.
  useEffect(() => {
    if (!currentNodeId) return;

    const regularIds = Object.values(selectedVariants)
      .flat()
      .filter((v) => v?.id)
      .map((v) => v.id);

    const allocIds = Object.values(selectedAllocableVariants || {})
      .flat()
      .filter((v) => v?.id)
      .map((v) => v.id);

    // combine and dedupe
    const ids = [...new Set([...regularIds, ...allocIds])];

    setAllVariantIds((prev) => {
      const prevForNode = nodeVariantMap[currentNodeId] || [];
      const others = prev.filter((id) => !prevForNode.includes(id));
      return [...new Set([...others, ...ids])];
    });

    setNodeVariantMap((prev) => ({ ...prev, [currentNodeId]: ids }));
  }, [selectedVariants, selectedAllocableVariants, currentNodeId, nodeVariantMap, setAllVariantIds]);

  // Fetch outputs
  useEffect(() => {
    if (!selectedworkflowId) return;
    const fetchOutputs = async () => {
      try {
        const res = await fetcher([
          `/new/workflow-app/first/process/outputs/?process_creation_id=${selectedworkflowId}`,
        ]);
        setarrayofoutputids(res || []);
      } catch (err) {
        console.error('Error fetching outputs:', err);
      }
    };
    fetchOutputs();
  }, [selectedworkflowId]);

  // ─── Handlers ──────────────────────────────────────────────────────────────────

  const handleAddVariantsDialog = () => {
    if (editRawMaterialInputs?.length) {
      const ids = editRawMaterialInputs.map((i) => i.value || i.id).filter(Boolean);
      setRawMaterialIds(ids);
      ids.forEach(fetchVariantsForRawMaterial);
    }
    setVariantDialogOpen(true);
  };

  const handleOpenAllocableDialog = () => {
    if (editRawMaterialInputs?.length) {
      const ids = editRawMaterialInputs.map((i) => i.value || i.id).filter(Boolean);
      setRawMaterialIds(ids); // reuse same rawMaterialIds
      ids.forEach(fetchAllocableVariantsForRawMaterial);
    }
    setAllocableVariantDialogOpen(true);
  };

  const handleSaveVariants = async () => {
    // 1. Collect ALL currently selected regular variants
    const regularIds = Object.values(selectedVariants)
      .flat()
      .filter((item) => item && item.id)
      .map((item) => item.id);

    // 2. Collect ALL currently selected allocable variants (don't lose them!)
    const allocableIds = Object.values(selectedAllocableVariants)
      .flat()
      .filter((item) => item && item.id)
      .map((item) => item.id);

    // 3. Combine both sets → remove duplicates
    const combinedVariantIds = [...new Set([...regularIds, ...allocableIds])];

    // 4. Prepare payload (same format as before)
    const payload = {
      output_ids: arrayofoutputids || [],
      product_variant_ids: combinedVariantIds,
    };

    try {
      // 5. Send to backend
      const res = await patchFetcher(
        ['/new/workflow-app/product/variant/add/in/output/',
          payload]
      );

      // 6. Update local state / maps (regular variants part)
      if (currentNodeId) {
        setNodeVariants((prev) => ({
          ...prev,
          [currentNodeId]: selectedVariants,
        }));

        setNodeVariantMap((prev) => ({
          ...prev,
          [currentNodeId]: combinedVariantIds,   // now includes both
        }));
      }

      // 7. Update parent / global variant list
      setAllVariantIds(combinedVariantIds);
      setselectedVariantsfromparent(selectedVariants); // keep sending regular map to parent

      // inform parent so it can update its combinedVariantIds state
      if (typeof onRegularVariantsSaved === 'function') {
        onRegularVariantsSaved(selectedVariants);
      }

      enqueueSnackbar('Variants saved successfully', { variant: 'success' });
      setVariantDialogOpen(false);

      return res;
    } catch (err) {
      console.error('Error saving variants:', err);
      enqueueSnackbar('Failed to save variants', { variant: 'error' });
      throw err;
    }
  };

  const handleSaveAllocableVariants = async () => {
    // 1. Collect currently selected allocable variants
    const allocableIds = Object.values(selectedAllocableVariants)
      .flat()
      .filter((item) => item && item.id)
      .map((item) => item.id);

    // 2. Collect ALL currently selected regular variants (don't lose them!)
    const regularIds = Object.values(selectedVariants)
      .flat()
      .filter((item) => item && item.id)
      .map((item) => item.id);

    // 3. Combine both sets → remove duplicates
    const combinedVariantIds = [...new Set([...regularIds, ...allocableIds])];

    // 4. Prepare payload
    const payload = {
      output_ids: arrayofoutputids || [],
      product_variant_ids: combinedVariantIds,
    };

    try {
      // 5. Send to backend
      const res = await patchFetcher(
        ['/new/workflow-app/product/variant/add/in/output/',
          payload]
      );

      // 6. Optional: update local node variant map with combined list
      if (currentNodeId) {
        setNodeVariantMap((prev) => ({
          ...prev,
          [currentNodeId]: combinedVariantIds,
        }));
      }

      // 7. Update global / parent variant list
      setAllVariantIds(combinedVariantIds);

      // inform parent so it can update its combinedVariantIds state
      if (typeof onAllocableVariantsSaved === 'function') {
        onAllocableVariantsSaved(selectedAllocableVariants);
      }

      // Note: we do NOT update selectedVariantsfromparent here,
      // because it is meant for regular variants only (your parent expects that structure)

      enqueueSnackbar('Allocable variants saved successfully', { variant: 'success' });
      setAllocableVariantDialogOpen(false);

      return res;
    } catch (err) {
      console.error('Error saving allocable variants:', err);
      enqueueSnackbar('Failed to save allocable variants', { variant: 'error' });
      throw err;
    }
  };

  const handleRawMaterialInputsChange = (_event, newValue) => {
    const prevIds = editRawMaterialInputs.map((i) => i.value || i.id).filter(Boolean);
    const newIds = newValue.map((i) => i.value || i.id).filter(Boolean);

    setEditRawMaterialInputs(newValue);
    setRawMaterialIds(newIds);

    const added = newIds.filter((id) => !prevIds.includes(id));
    added.forEach(fetchVariantsForRawMaterial);

    setSelectedVariants((prev) => {
      const next = { ...prev };
      added.forEach((id) => {
        if (!next[id]) next[id] = [];
      });
      prevIds.filter((id) => !newIds.includes(id)).forEach((id) => {
        delete next[id];
      });
      return next;
    });
  };

  const handleUpdateNodeWithVariants = () => {
    if (!currentNodeId || !currentNodeName?.trim()) return;
    updateSelectedVariants(selectedVariants);
    setselectedVariantsfromparent(selectedVariants);
    setFieldsEnabled(true);
    handleUpdateNode();
  };

  const handleVariantChange = (rawMaterialId, newValue) => {
    updateSelectedVariants({
      ...selectedVariants,
      [rawMaterialId]: newValue ? [newValue] : [],
    });
  };

  const handleAllocableVariantChange = (rawMaterialId, newValue) => {
    setSelectedAllocableVariants((prev) => ({
      ...prev,
      [rawMaterialId]: newValue || null,
    }));
  };


  const getSingleVariantValue = (rawMaterialId) => {
    const vs = selectedVariants[rawMaterialId] || [];
    return vs.length > 0 ? vs[0] : null;
  };

  const getAllocableValue = (rawMaterialId) => selectedAllocableVariants[rawMaterialId] || [];
  // ─── Render helpers ────────────────────────────────────────────────────────────
  const renderFetchedFormulas = () => {
    if (!fetchedNodeData) return null;

    const inputForeign = fetchedNodeData.input_foreign?.[0];
    const outputForeign = fetchedNodeData.output_foreign || [];
    const costs = fetchedNodeData.cost_foreign || [];

    return (
      <Box sx={{ mb: 2, p: 2, bgcolor: SECTION_BG, borderRadius: RADIUS, border: BORDER }}>
        <Typography sx={{ fontWeight: 700, fontSize: '12px', color: TEXT_SUB, mb: 1, textTransform: 'uppercase' }}>
          Fetched Node Data
        </Typography>

        <Stack spacing={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>ID</Typography>
            <Chip label={fetchedNodeData.id} size="small" color="primary" />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>Type</Typography>
            <Chip label={fetchedNodeData.type} size="small" color="primary" />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>Name</Typography>
            <Chip label={fetchedNodeData.name} size="small" color="primary" />
          </Box>
        </Stack>

        {inputForeign && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography sx={{ fontWeight: 600, fontSize: '12px', mb: 1 }}>
              Input: <strong>{inputForeign.name}</strong>
            </Typography>
            {Array.isArray(inputForeign.formula) && inputForeign.formula.length > 0 && (
              inputForeign.formula.map((f, i) => (
                <Box key={i} sx={{ bgcolor: '#fff', p: 1.5, borderRadius: '8px', mb: 1, border: BORDER }}>
                  <Typography variant="caption" color="text.secondary">{f.name}</Typography>
                  <Typography sx={{ fontFamily: 'monospace', color: ACCENT }}>
                    {reconstructFormulaString(f.formula)}
                  </Typography>
                </Box>
              ))
            )}
          </>
        )}

        {outputForeign.length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography sx={{ fontWeight: 600, fontSize: '12px', mb: 1 }}>Output Formulas</Typography>
            {outputForeign.map((out, i) => (
              <Box key={i} sx={{ bgcolor: '#fff', p: 1.5, borderRadius: '8px', mb: 1, border: BORDER }}>
                <Typography variant="caption" color="text.secondary">{out.name}</Typography>
                <Typography sx={{ fontFamily: 'monospace', color: ACCENT }}>
                  {reconstructFormulaString(out.formula)}
                </Typography>
              </Box>
            ))}
          </>
        )}

        {costs.length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography sx={{ fontWeight: 600, fontSize: '12px', mb: 1 }}>Costs</Typography>
            {costs.map((c, i) => (
              <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                <Typography>{c.name}</Typography>
                <Typography>{c.value?.amount} {c.value?.unit}</Typography>
              </Box>
            ))}
          </>
        )}
      </Box>
    );
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 380,
          background: '#f4f4f8',
          borderLeft: BORDER,
          boxShadow: '-8px 0 32px rgba(0,0,0,0.08)',
        }
      }}
    >
      {/* Header */}
      <Box sx={{
        px: 2.5, py: 2,
        background: '#fff',
        borderBottom: BORDER,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: '16px', letterSpacing: '-0.02em' }}>
            Edit Node
          </Typography>
          <Typography sx={{ fontSize: '11.5px', color: TEXT_SUB, mt: '1px' }}>
            {selectedNode?.data?.label || 'Unknown Node'}
          </Typography>
          {currentNodeId && (
            <Typography sx={{ fontSize: '10px', color: TEXT_SUB, mt: 0.5 }}>
              ID: {currentNodeId}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Delete Node">
            <IconButton
              onClick={handleDeleteSelectedNode}
              disabled={!selectedNode}
              size="small"
              sx={{ color: DANGER, background: DANGER_LIGHT, '&:hover': { background: '#fecaca' } }}
            >
              <Iconify icon="mdi:trash-can-outline" width={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton
              onClick={onClose}
              size="small"
              sx={{ color: TEXT_SUB, background: SECTION_BG, '&:hover': { background: '#e4e4e7' } }}
            >
              <Iconify icon="mdi:close" width={18} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
        {(fetchingNodeData || loadingNodeData) ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: ACCENT }} />
          </Box>
        ) : (
          <>
            {fetchedNodeData ? renderFetchedFormulas() : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">No node data available.</Typography>
              </Box>
            )}
          </>
        )}

        <SectionCard title="Node Settings" icon="mdi:pencil-outline">
          <PillInput
            label="Node Name"
            value={editNodeName}
            onChange={(e) => setEditNodeName(e.target.value)}
            disabled={!fieldsEnabled}
          />
        </SectionCard>

        <SectionCard title="Input Type" icon="mdi:import">
          <RadioGroup
            row
            value={editNodeInputType}
            onChange={(e) => setEditNodeInputType(e.target.value)}
            sx={{ gap: 1 }}
          >
            {['raw', 'previous', 'utility'].map((type) => (
              <FormControlLabel
                key={type}
                value={type}
                control={<Radio size="small" disabled={!fieldsEnabled} />}
                label={<Typography sx={{ fontSize: '12.5px' }}>{type === 'raw' ? 'Raw Material' : type === 'previous' ? 'Prev. Node' : 'Utility'}</Typography>}
                sx={{
                  m: 0, px: 1.5, py: 0.8, borderRadius: '10px', border: BORDER,
                  background: editNodeInputType === type ? ACCENT_LIGHT : SECTION_BG,
                }}
              />
            ))}
          </RadioGroup>

          <Box sx={{ mt: 2 }}>
            {editNodeInputType === 'raw' && (
              <PillAutocomplete
                label="Raw Material Inputs"
                options={inventoryOptions || []}
                value={editRawMaterialInputs}
                onChange={handleRawMaterialInputsChange}
                disabled={!fieldsEnabled}
              />
            )}
            {editNodeInputType === 'previous' && (
              <PillAutocomplete
                label="Previous Node Inputs"
                options={sourceNodeOptions || []}
                value={editPreviousNodeInputs}
                onChange={(_e, v) => setEditPreviousNodeInputs(v)}
                disabled={!fieldsEnabled}
              />
            )}
            {editNodeInputType === 'utility' && (
              <PillAutocomplete
                label="Utility Inputs"
                options={utilityOptions || []}
                value={selectedUtilities}
                onChange={(_e, v) => setEditUtilityInputs(v)}
                disabled={!fieldsEnabled}
              />
            )}
          </Box>
        </SectionCard>

        {costOptions && (
          <SectionCard title="Costs" icon="mdi:currency-usd">
            <PillAutocomplete
              label="Costs"
              options={costOptions}
              value={editNodeCost}
              onChange={(_e, v) => setEditNodeCost(v || [])}
              disabled={!fieldsEnabled}
            />
          </SectionCard>
        )}

        {Object.keys(selectedVariants).length > 0 && (
          <SectionCard title="Selected Variants" icon="mdi:database">
            {Object.entries(selectedVariants).map(([id, vars]) => (
              <Box key={id} sx={{ mb: 2, p: 1.5, bgcolor: SECTION_BG, borderRadius: '8px' }}>
                <Typography sx={{ fontSize: '11px', fontWeight: 700, color: TEXT_SUB, mb: 0.5 }}>
                  Raw Material ID: {id}
                </Typography>
                {vars.length > 0 ? vars.map((v, i) => (
                  <Chip key={i} label={v.name || `Variant ${v.id}`} size="small" sx={{ mr: 1, mb: 1 }} />
                )) : (
                  <Typography variant="body2" color="text.secondary">No variants selected</Typography>
                )}
              </Box>
            ))}
          </SectionCard>
        )}

        {hasrawmaterial && (
          <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
            <ActionButton onClick={handleAddVariantsDialog} icon="mdi:plus">
              Add Variants
            </ActionButton>

            <ActionButton
              onClick={handleOpenAllocableDialog}
              icon="mdi:view-grid-plus"
              color="secondary"
            >
              Add Allocable Variant
            </ActionButton>
          </Stack>
        )}

        <Stack spacing={1.5} sx={{ mb: 2 }}>
          <ActionButton
            onClick={handleUpdateNodeWithVariants}
            disabled={!editNodeName.trim()}
            icon="mdi:content-save-outline"
          >
            {isEditing ? 'Update Node' : 'Save Node'}
          </ActionButton>

          <ActionButton
            onClick={handleErrorEdgeButtonClick}
            disabled={isErrorEdgeMode}
            icon="mdi:vector-line"
            danger
            variant="outlined"
          >
            Create Error Edge
          </ActionButton>

          <Typography variant="caption" color="text.secondary" align="center">
            Click, then click another node to draw a red error edge
          </Typography>
        </Stack>

        <SectionCard title="Connections" icon="mdi:transit-connection-variant">
          {selectedEdgeId && (
            <Box sx={{ mb: 2 }}>
              <PillInput
                label="Edge Label"
                value={editEdgeLabel}
                onChange={(e) => setEditEdgeLabel(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                disabled
              />
              <Box sx={{ mt: 1.5 }}>
                <ActionButton
                  onClick={() => handleAddOrEditEdgeLabel(selectedEdgeId)}
                  disabled={!editEdgeLabel.trim()}
                  icon="mdi:content-save-outline"
                >
                  Save Label
                </ActionButton>
              </Box>
            </Box>
          )}

          {connectedEdges.length > 0 ? (
            <Stack spacing={1.5}>
              {connectedEdges.map(edge => (
                <Box key={edge.id} sx={{
                  background: edge.isErrorEdge ? DANGER_LIGHT : SECTION_BG,
                  border: `1.5px solid ${edge.isErrorEdge ? '#fca5a5' : '#e4e4e7'}`,
                  borderRadius: '10px',
                  px: 2, py: 1.5,
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.8 }}>
                    {edge.isErrorEdge && (
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: DANGER }} />
                    )}
                    <Typography sx={{ fontSize: '12.5px', fontWeight: 700, color: edge.isErrorEdge ? DANGER : TEXT_MAIN }}>
                      {edge.isErrorEdge ? 'Error Edge' : `Edge ${edge.id}`}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '11.5px', color: TEXT_SUB }}>
                    From: {edge.sourceNode} ({edge.sourcePort})
                  </Typography>
                  <Typography sx={{ fontSize: '11.5px', color: TEXT_SUB }}>
                    To: {edge.targetNode} ({edge.targetPort})
                  </Typography>
                  {edge.label && (
                    <Chip label={edge.label} size="small" sx={{
                      height: 20, fontSize: '10.5px', mt: 0.8,
                      background: ACCENT_LIGHT, color: ACCENT, fontWeight: 600, borderRadius: '5px',
                    }} />
                  )}
                  <Box sx={{ display: 'flex', gap: 1, mt: 1.2 }}>
                    <Button
                      size="small"
                      onClick={() => handleRemoveEdge(edge.id)}
                      sx={{
                        borderRadius: PILL, textTransform: 'none', fontWeight: 600,
                        fontSize: '11.5px', color: DANGER, border: `1px solid ${DANGER}44`,
                        px: 1.5, py: 0.4,
                        '&:hover': { background: DANGER_LIGHT },
                      }}
                    >
                      Remove
                    </Button>
                    <Button
                      size="small"
                      onClick={() => { setSelectedEdgeId(edge.id); setEditEdgeLabel(edge.label || ''); }}
                      sx={{
                        borderRadius: PILL, textTransform: 'none', fontWeight: 600,
                        fontSize: '11.5px', color: ACCENT, border: `1px solid ${ACCENT}44`,
                        px: 1.5, py: 0.4,
                        '&:hover': { background: ACCENT_LIGHT },
                      }}
                    >
                      {edge.label ? 'Edit Label' : 'Add Label'}
                    </Button>
                  </Box>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography sx={{ fontSize: '13px', color: TEXT_SUB, textAlign: 'center', py: 1 }}>
              No connections for this node.
            </Typography>
          )}
        </SectionCard>
        {/* ── Regular Variants Dialog ── */}
        <Dialog open={variantDialogOpen} onClose={() => setVariantDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add Variants</DialogTitle>
          <DialogContent>
            {rawMaterialIds.length === 0 ? (
              <Typography>No raw materials selected.</Typography>
            ) : (
              <Stack spacing={2}>
                {rawMaterialIds.map((id) => (
                  <Box key={id} sx={{ p: 2, bgcolor: SECTION_BG, borderRadius: RADIUS }}>
                    <Typography fontWeight={600}>Raw Material ID: {id}</Typography>
                    <Autocomplete
                      options={variantOptions[id] || []}
                      getOptionLabel={(opt) => opt.name ? `${opt.name} (₹${opt.calculated_cost ?? '?'})` : ''}
                      value={getSingleVariantValue(id)}
                      onChange={(_e, val) => handleVariantChange(id, val)}
                      loading={loadingVariants[id]}
                      renderInput={(params) => (
                        <TextField {...params} label="Select Variant" size="small" />
                      )}
                    />
                  </Box>
                ))}
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setVariantDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveVariants}>Save</Button>
          </DialogActions>
        </Dialog>

        {/* ── Allocable Variants Dialog ── */}
        <Dialog
          open={allocableVariantDialogOpen}
          onClose={() => setAllocableVariantDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { borderRadius: RADIUS, border: BORDER, boxShadow: CARD_SHADOW }
          }}
        >
          <DialogTitle sx={{ fontWeight: 700, borderBottom: BORDER }}>
            Add Allocable Variants
          </DialogTitle>

          <DialogContent sx={{ pt: 3 }}>
            <Typography sx={{ mb: 2.5, color: TEXT_SUB, fontSize: '13.5px' }}>
              Select one or more allocable variants for each raw material
            </Typography>

            {rawMaterialIds.length === 0 ? (
              <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                No raw materials selected yet.
              </Typography>
            ) : (
              <Stack spacing={2.5}>
                {rawMaterialIds.map((rmId) => (
                  <Paper
                    key={rmId}
                    variant="outlined"
                    sx={{ p: 2, borderRadius: RADIUS, bgcolor: SECTION_BG }}
                  >
                    <Typography sx={{ fontWeight: 600, mb: 1.5 }}>
                      Raw Material ID: {rmId}
                    </Typography>

                    <Autocomplete
                      multiple
                      options={allocableVariantOptions[rmId] || []}
                      getOptionLabel={(opt) =>
                        opt?.name
                          ? `${opt.name}  (₹${opt.calculated_cost?.toFixed(2) ?? '—'})`
                          : '—'
                      }
                      value={getAllocableValue(rmId)}
                      onChange={(_e, newValue) => handleAllocableVariantChange(rmId, newValue)}
                      loading={loadingAllocable[rmId]}
                      isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Allocable Variants"
                          size="small"
                          placeholder="Select one or more..."
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingAllocable[rmId] && <CircularProgress size={18} />}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': { borderRadius: PILL },
                          }}
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            key={option.id}
                            label={option.name}
                            size="small"
                            {...getTagProps({ index })}
                            sx={{ background: ACCENT_LIGHT, color: ACCENT }}
                          />
                        ))
                      }
                    />
                  </Paper>
                ))}
              </Stack>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 2.5, borderTop: BORDER }}>
            <Button
              onClick={() => setAllocableVariantDialogOpen(false)}
              sx={{ borderRadius: PILL }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveAllocableVariants}
              disabled={
                Object.values(selectedAllocableVariants).every((arr) => !arr?.length)
              }
              sx={{ borderRadius: PILL }}
            >
              Save Allocable Variants
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Drawer>
  );
};

NodePropertiesDrawer2ndStep.propTypes = {
  open: PropTypes.any,
  onClose: PropTypes.any,
  selectedNode: PropTypes.any,
  loadingNodeData: PropTypes.any,
  allVariantIds: PropTypes.any,
  setAllVariantIds: PropTypes.any,
  editNodeName: PropTypes.any,
  setEditNodeName: PropTypes.any,
  editNodeInputType: PropTypes.any,
  setEditNodeInputType: PropTypes.any,
  editRawMaterialInputs: PropTypes.any,
  setEditRawMaterialInputs: PropTypes.any,
  editPreviousNodeInputs: PropTypes.any,
  setEditPreviousNodeInputs: PropTypes.any,
  editUtilityInputs: PropTypes.any,
  setEditUtilityInputs: PropTypes.any,
  editNodeCost: PropTypes.any,
  setEditNodeCost: PropTypes.any,
  inventoryOptions: PropTypes.any,
  utilityOptions: PropTypes.any,
  costOptions: PropTypes.any,
  sourceNodeOptions: PropTypes.any,
  connectedEdges: PropTypes.any,
  selectedEdgeId: PropTypes.any,
  setSelectedEdgeId: PropTypes.any,
  editEdgeLabel: PropTypes.any,
  setEditEdgeLabel: PropTypes.any,
  handleUpdateNode: PropTypes.any,
  handleErrorEdgeButtonClick: PropTypes.any,
  handleDeleteSelectedNode: PropTypes.any,
  handleAddOrEditEdgeLabel: PropTypes.any,
  handleRemoveEdge: PropTypes.any,
  handleOutputDialog: PropTypes.any,
  handleInputFormulaDialog: PropTypes.any,
  handleRawMaterialDialog: PropTypes.any,
  handleUtilityDialog: PropTypes.any,
  handleCostDialog: PropTypes.any,
  isErrorEdgeMode: PropTypes.any,
  setselectedVariantsfromparent: PropTypes.any,
  selectedVariantsfromparent: PropTypes.any,
  seteditoutputids: PropTypes.any,
  editoutputids: PropTypes.any,
  setutitilityfromdrawer: PropTypes.any,
  nodes: PropTypes.any,
  isEditing: PropTypes.any,
  setIsEditing: PropTypes.any,
  clickednodes: PropTypes.any,
  setclickednodes: PropTypes.any,
  selectedworkflowId: PropTypes.any,
};

export default NodePropertiesDrawer2ndStep;